// Web MIDI input module: emits normalized events { type, channel, note, velocity, cc, value, t }.
const PREFERRED_KEY = 'spore.midi.preferredInput';

export function parseMidiMessage(data, t) {
  if (!data || data.length < 2) return null;
  const status = data[0];
  const cmd = status & 0xf0;
  const channel = (status & 0x0f) + 1;
  const d1 = data[1];
  const d2 = data.length > 2 ? data[2] : 0;
  if (cmd === 0x90) {
    return d2 > 0
      ? { type: 'note_on', channel, note: d1, velocity: d2, cc: null, value: null, t }
      : { type: 'note_off', channel, note: d1, velocity: 0, cc: null, value: null, t };
  }
  if (cmd === 0x80) return { type: 'note_off', channel, note: d1, velocity: d2, cc: null, value: null, t };
  if (cmd === 0xb0) return { type: 'cc', channel, note: null, velocity: null, cc: d1, value: d2, t };
  return null;
}

export function pickInput(inputs, preferredId) {
  if (!inputs.length) return null;
  return (
    inputs.find((i) => i.id === preferredId) ||
    inputs.find((i) => /biotron/i.test(i.name || '')) ||
    inputs[0]
  );
}

export function createMidiManager(onEvent, { nav = typeof navigator !== 'undefined' ? navigator : undefined, storage = window.localStorage, now = () => performance.now() } = {}) {
  const supported = !!(nav && typeof nav.requestMIDIAccess === 'function');
  const listeners = new Set();
  let access = null;
  let current = null;
  let preferredId = storage.getItem(PREFERRED_KEY);
  const info = {
    supported,
    status: supported ? 'idle' : 'unsupported',
    deviceName: null,
    selectedId: null,
    inputs: [],
    lastDeviceName: null,
    error: null,
    disconnects: 0,
    reconnects: 0,
  };

  const notify = () => listeners.forEach((fn) => fn(getInfo()));

  function detach() {
    if (current) current.onmidimessage = null;
    current = null;
  }

  function attach(input) {
    detach();
    current = input;
    input.onmidimessage = (msg) => {
      const ev = parseMidiMessage(msg.data, now());
      if (ev) onEvent(ev);
    };
    info.selectedId = input.id;
    info.deviceName = input.name;
    info.lastDeviceName = input.name;
    info.status = 'connected';
  }

  function refresh() {
    if (!access) return;
    const inputs = [];
    access.inputs.forEach((i) => {
      if (i.state !== 'disconnected') inputs.push(i);
    });
    info.inputs = inputs.map((i) => ({ id: i.id, name: i.name, manufacturer: i.manufacturer }));
    const stillThere = current && inputs.find((i) => i.id === current.id);
    if (stillThere && (!preferredId || preferredId === current.id || !inputs.find((i) => i.id === preferredId))) {
      notify();
      return;
    }
    const wasConnected = info.status === 'connected';
    const target = pickInput(inputs, preferredId);
    if (target) {
      if (!wasConnected && info.lastDeviceName) info.reconnects += 1;
      attach(target);
    } else {
      if (wasConnected) info.disconnects += 1;
      detach();
      info.status = 'no-device';
      info.deviceName = null;
      info.selectedId = null;
    }
    notify();
  }

  async function connect() {
    if (!supported) {
      info.status = 'unsupported';
      notify();
      return getInfo();
    }
    info.status = 'requesting';
    info.error = null;
    notify();
    try {
      access = await nav.requestMIDIAccess({ sysex: false });
      access.onstatechange = () => refresh();
      refresh();
    } catch (e) {
      info.status = e && (e.name === 'SecurityError' || e.name === 'NotAllowedError') ? 'permission-denied' : 'error';
      info.error = e ? `${e.name}: ${e.message}` : 'unknown error';
      notify();
    }
    return getInfo();
  }

  async function autoConnectIfGranted() {
    if (!supported || !nav.permissions) return;
    try {
      const p = await nav.permissions.query({ name: 'midi', sysex: false });
      if (p.state === 'granted') await connect();
      else if (p.state === 'denied') {
        info.status = 'permission-denied';
        notify();
      }
    } catch {
      /* permissions API without midi support: wait for the Connect button */
    }
  }

  function select(id) {
    preferredId = id;
    storage.setItem(PREFERRED_KEY, id);
    detach();
    info.status = 'no-device';
    refresh();
  }

  function getInfo() {
    return { ...info, inputs: [...info.inputs] };
  }

  return {
    connect,
    autoConnectIfGranted,
    select,
    refresh,
    getInfo,
    subscribe(fn) {
      listeners.add(fn);
      return () => listeners.delete(fn);
    },
  };
}
